import { useState, useRef, useEffect, useMemo } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackEvent } from "@/utils/analytics";
import StepWizard from "./components/StepWizard";
import RadarChart from "./components/RadarChart";
import LeadUnlockSection from "./components/LeadUnlockSection";
import SystemSelector, { type SystemType } from "./components/SystemSelector";
import CardForm, { type CardAnswers } from "./components/CardForm";
import NewDealForm, { type NewDealAnswers } from "./components/NewDealForm";
import ResultSection, {
  calculateCardResults,
  calculateNewDealResults,
  type ResultItem,
} from "./components/ResultSection";
import CounselForm from "./components/CounselForm";
import {
  evaluateDiagnosis,
  type DiagnosisAnswers,
  type DiagnosisResult,
} from "@/utils/diagnosisEngine";

const DEFAULT_DIAGNOSIS_ANSWERS: DiagnosisAnswers = {
  q1_track: "",
  q2_experience: "",
  q3_stack: "",
  q4_commitment: "",
  q5_goal: "",
};

const defaultCardAnswers: CardAnswers = {
  공무원: "",
  카드나이: "",
  부정수급: "",
};

const defaultNewDealAnswers: NewDealAnswers = {
  국적: "",
  나이: "",
  군필: false,
  근로: "",
  사업자: "",
  학적: "",
  구직등록: "",
  타훈련: "",
  이력: "",
  수급: "",
};

type DiagnosisMode = "career_stat" | "eligibility_check";

export default function SelfDiagnosisPage() {
  const [mode, setMode] = useState<DiagnosisMode>("career_stat");

  // Mode 1: 5-Question Career Stat Wizard
  const [wizardAnswers, setWizardAnswers] = useState<DiagnosisAnswers>(() => {
    try {
      const saved = localStorage.getItem("dabom_career_diagnosis_answers_v2");
      return saved ? JSON.parse(saved) : DEFAULT_DIAGNOSIS_ANSWERS;
    } catch {
      return DEFAULT_DIAGNOSIS_ANSWERS;
    }
  });
  const [diagnosisResult, setDiagnosisResult] = useState<DiagnosisResult | null>(null);
  const resultContainerRef = useRef<HTMLDivElement>(null);

  // Mode 2: Eligibility Check (Card vs NewDeal)
  const [system, setSystem] = useState<SystemType>("카드");
  const [cardAnswers, setCardAnswers] = useState<CardAnswers>(defaultCardAnswers);
  const [newDealAnswers, setNewDealAnswers] = useState<NewDealAnswers>(defaultNewDealAnswers);
  const [showEligibilityResult, setShowEligibilityResult] = useState(false);

  usePageMeta({
    title: "내 커리어 스탯 자가진단 & 국비 지원 적합도 — 다봄",
    description:
      "채용공고 4,012건과 고용순증 2,611개사 실측 데이터 기반. 전화번호 입력 불필요, 30초 즉시 확인.",
    path: "/자가진단",
  });

  // Save wizard answers in localStorage
  useEffect(() => {
    try {
      localStorage.setItem("dabom_career_diagnosis_answers_v2", JSON.stringify(wizardAnswers));
    } catch {
      // ignore
    }
  }, [wizardAnswers]);

  const handleWizardFieldChange = (field: keyof DiagnosisAnswers, value: string) => {
    setWizardAnswers((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWizardComplete = () => {
    const result = evaluateDiagnosis(wizardAnswers);
    setDiagnosisResult(result);
    trackEvent("career_diagnosis_complete");

    setTimeout(() => {
      if (resultContainerRef.current) {
        resultContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const handleWizardReset = () => {
    setDiagnosisResult(null);
    setWizardAnswers(DEFAULT_DIAGNOSIS_ANSWERS);
    try {
      localStorage.removeItem("dabom_career_diagnosis_answers_v2");
    } catch {
      // ignore
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Eligibility logic
  const eligibilityItems: ResultItem[] = useMemo(() => {
    if (!showEligibilityResult) return [];
    return system === "카드"
      ? calculateCardResults(cardAnswers)
      : calculateNewDealResults(newDealAnswers);
  }, [showEligibilityResult, system, cardAnswers, newDealAnswers]);

  const eligibilitySummary = useMemo(() => {
    if (!showEligibilityResult) return "";
    const hasBlock = eligibilityItems.some((i) => i.status === "block");
    const hasConditional = eligibilityItems.some((i) => i.status === "conditional");
    if (hasBlock) return "제한 항목 있음";
    if (hasConditional) return "추가 확인 필요";
    return "요건 충족";
  }, [showEligibilityResult, eligibilityItems]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-12">
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-50 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300 border border-brand-200 dark:border-brand-800 mb-3">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          30만 건 실측 통계 기반 · 전화번호 불필요 30초 즉시 확인
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
          내 IT 커리어 스탯 자가진단
        </h1>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mt-2.5 max-w-2xl mx-auto leading-relaxed break-keep">
          채용공고 4,012건, 국민연금 2,611개 사업장, 직업훈련 35,140건 공공 데이터를 바탕으로 내 목표 직군 역량과 합격 가능성을 30초 만에 무료로 진단합니다.
        </p>

        {/* Feature Highlights Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-4 text-xs font-medium text-gray-600 dark:text-gray-400">
          <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            ⚡ 30초 즉시 확인
          </span>
          <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            🔒 전화번호 입력 불필요
          </span>
          <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            ✓ 영업 전화 0건 안심
          </span>
          <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            📊 실시간 듀얼 레이더 차트
          </span>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="flex items-center justify-center mt-6">
          <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setMode("career_stat")}
              className={`px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all cursor-pointer ${
                mode === "career_stat"
                  ? "bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:text-gray-400"
              }`}
            >
              🎯 5문항 커리어 스탯 진단 (스모어 폼)
            </button>
            <button
              type="button"
              onClick={() => setMode("eligibility_check")}
              className={`px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all cursor-pointer ${
                mode === "eligibility_check"
                  ? "bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:text-gray-400"
              }`}
            >
              📋 국비 지원 자격 요건 판정
            </button>
          </div>
        </div>
      </div>

      {/* MODE 1: 5-Question Career Stat Wizard */}
      {mode === "career_stat" && (
        <div className="space-y-8">
          {!diagnosisResult ? (
            <StepWizard
              answers={wizardAnswers}
              onAnswerChange={handleWizardFieldChange}
              onComplete={handleWizardComplete}
            />
          ) : (
            <div ref={resultContainerRef} className="space-y-8 animate-fadeIn">
              {/* Reset / Re-diagnose Top Button */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleWizardReset}
                  className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-sm" />
                  문항 다시 답변하기
                </button>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                  <i className="ri-check-double-line"></i>
                  진단 완료 (공공데이터 실측 매칭)
                </span>
              </div>

              {/* 2. Wanted Style 5-Axis Radar Chart */}
              <RadarChart
                radarData={diagnosisResult.radarData}
                overallScore={diagnosisResult.overallScore}
                percentile={diagnosisResult.percentile}
                trackName={diagnosisResult.trackName}
                trackBadge={diagnosisResult.trackBadge}
                costKrw={diagnosisResult.costKrw}
                elapsedMs={diagnosisResult.elapsedMs}
              />

              {/* 4. Option B Lead Unlock Form & In-depth Report */}
              <LeadUnlockSection
                result={diagnosisResult}
                onReset={handleWizardReset}
              />
            </div>
          )}
        </div>
      )}

      {/* MODE 2: Eligibility Check (Card vs New Deal) */}
      {mode === "eligibility_check" && (
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs md:text-sm text-gray-700 leading-relaxed dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
            <strong>국민내일배움카드와 K-뉴딜 아카데미 자격 요건 판정:</strong> 고용24 제도안내 및 고용노동부고시 규정에 따라 결격 사유를 검토합니다.
          </div>

          <SystemSelector
            value={system}
            onChange={(s) => {
              setSystem(s);
              setShowEligibilityResult(false);
            }}
          />

          {!showEligibilityResult && system === "카드" && (
            <CardForm
              answers={cardAnswers}
              onChange={setCardAnswers}
              onSubmit={() => setShowEligibilityResult(true)}
            />
          )}

          {!showEligibilityResult && system === "뉴딜" && (
            <NewDealForm
              answers={newDealAnswers}
              onChange={setNewDealAnswers}
              onSubmit={() => setShowEligibilityResult(true)}
            />
          )}

          {showEligibilityResult && (
            <div className="space-y-6">
              <ResultSection
                system={system}
                items={eligibilityItems}
                onReset={() => setShowEligibilityResult(false)}
              />

              <CounselForm
                resultSummary={eligibilitySummary}
                resultItems={eligibilityItems}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
